import { Box, Button, Typography } from "@mui/material";
import React, { useContext } from "react";
import theme from "../theme";
import { useNavigate } from "react-router-dom";
import { AutoTaskAuthContext } from "../Context/AutoTaskContext";

// Icons
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import { ReactComponent as WhatsAppIcon } from "../assets/icons/whatsapp.svg";

const TicketReceipt = ({ ticketInfo, assignInfo }) => {
  const contextData = useContext(AutoTaskAuthContext);
  const { allStores = [], userInfo = {} } = contextData;
  const navigate = useNavigate();
  const ticketRaisedForStore = allStores?.find(
    (store) => Number(store?.store_id) === Number(ticketInfo?.store_id)
  )?.store_name;
  const formatDate = (dateStr) =>
    new Date(dateStr)
      .toLocaleString("en-GB", { day: "numeric", month: "long" })
      .replace(/\b(\d+)\b/, (d) => {
        const n = parseInt(d);
        const suffix =
          n >= 11 && n <= 13
            ? "th"
            : { 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th";
        return `${n}${suffix}`;
      });
  const deadline = formatDate(ticketInfo.deadline);

  const statusMapping = {
    open: "Open",
    resolved: "Resolved",
    in_progress: "Pending",
    follow_up: "Follow Up",
  };

  // Share ticket via WhatsApp
  const handleWhatsAppShare = () => {
    if (!ticketInfo) return;

    const message = `Ticket Number: *${ticketInfo.ticket_number}*\nStore: *${ticketInfo.store_id}*-*${ticketRaisedForStore}*\nDescription: *${ticketInfo.description}*`;
    const encodedMessage = encodeURIComponent(message);

    // WhatsApp link for sending message
    const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;

    // Open WhatsApp in a new tab
    window.open(whatsappUrl, "_blank");
  };

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        background: "linear-gradient(180deg, #008080 0%, #4428A0 100%)",
        borderRadius: 3,
        position: "relative",
      }}
    >
      {/* Tick Icon */}
      <Box
        sx={{
          position: "absolute",
          top: -36,
          left: "50%",
          transform: "translateX(-50%)",
          width: 72,
          height: 72,
          borderRadius: "50%",
          bgcolor: "#0E830A",

          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10,
        }}
      >
        <DoneRoundedIcon sx={{ fontSize: 48, fill: "#fff" }} />
      </Box>

      {/* Ticket Info */}
      {ticketInfo && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            flexDirection: "column",
            gap: 1,
            width: "100%",
            p: 1,
            color: "#fff",
          }}
        >
          {/* Arc Cutout */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: "50%",
              transform: "translateX(-50%)",
              width: 84,
              height: 40,
              bgcolor: "#928585c2",
              borderBottomLeftRadius: 100,
              borderBottomRightRadius: 100,
              zIndex: 1,
            }}
          />

          {/* Ticket Number */}
          <Typography
            sx={{
              fontSize: 24,
              color: "white",
              fontWeight: 600,
              alignSelf: "flex-end",

              width: "37.5%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {ticketInfo.ticket_number}
          </Typography>

          {/* Dashed Line */}
          <Box
            sx={{
              width: "100%",
              height: 2,
              borderBottom: "2px dashed white",
            }}
          />

          {/* Additional Ticket Info Container */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              flexDirection: "column",
              px: 2.5,
              mt: 1.5,
            }}
          >
            {/* Store Name and Status */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent:
                  userInfo.store_id === 0 && Number(ticketInfo.store_id) !== 0
                    ? "space-between"
                    : "center",
                width: "100%",
                mb: 1.5,
              }}
            >
              {/* Store Name */}
              {userInfo.store_id === 0 && Number(ticketInfo.store_id) !== 0 && (
                <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                  Store:{" "}
                  <span style={{ fontSize: 14, fontWeight: 400 }}>
                    {ticketRaisedForStore}
                  </span>
                </Typography>
              )}

              {/* Status */}
              {assignInfo && (
                <Box
                  sx={{
                    alignSelf: "flex-start",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 1,
                    py: 0.5,
                    bgcolor: theme.lightTheme.statuses[assignInfo.status].bgCol,
                    border: `1px solid ${theme.lightTheme.statuses[assignInfo.status].borderCol
                      }`,
                    borderRadius: 5,

                    height: 36,
                    minWidth: 96,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 14,
                      color:
                        theme.lightTheme.statuses[assignInfo.status].textCol,
                    }}
                  >
                    {statusMapping[assignInfo.status]}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Ticket Successfully Raised and/or Assigned Message */}
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                mb: 1.5,
              }}
            >
              Ticket Created {assignInfo ? "and Assigned" : ""} Successfully!
            </Typography>

            {/* Description Container */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                flexDirection: "column",
                mb: 1,
              }}
            >
              {/* Description Heading */}
              <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                Description:
              </Typography>

              {/* Description Details */}
              <Typography sx={{ fontSize: 14 }}>
                {ticketInfo.description}
              </Typography>
            </Box>

            {/* Deadline */}
            <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>
              Deadline:{" "}
              <span style={{ fontSize: 14, fontWeight: 400 }}>{deadline}</span>
            </Typography>

            {/* Create Another Ticket Button */}
            {ticketInfo && (
              <Button
                sx={{
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                  gap: 0.75,

                  backgroundColor: "#fff",
                  color: "#008080",
                }}
                fullWidth
                onClick={() => navigate("/all-service-tickets")}
              >
                <Typography sx={{ fontSize: 14 }}>Add one more</Typography>
                <AddRoundedIcon sx={{ fontSize: 24, fill: "#008080" }} />
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* Message for Resolved Ticket */}
      {ticketInfo.status === "resolved" && (
        <Typography sx={{ mt: "40px" }}>
          Ticket has been resolved successfully
        </Typography>
      )}

      {/* Go Home & Share via WhatsApp Button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 5,
          width: "100%",
          mt: 1.5,
          mb: 7,
        }}
      >
        {/* Go Home */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            height: 42,
            width: 42,
            backgroundColor: "#fff",
            borderRadius: 100,
            cursor: "pointer",
          }}
          onClick={() => navigate("/home")}
        >
          <HomeRoundedIcon sx={{ fontSize: 28, fill: "#008080" }} />
        </Box>

        {/* Share via WhatsApp */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            height: 42,
            width: 42,
            backgroundColor: "#fff",
            borderRadius: 100,
            cursor: "pointer",
          }}
          onClick={handleWhatsAppShare}
        >
          <WhatsAppIcon sx={{ fontSize: 28, fill: "#008080" }} />
        </Box>
      </Box>

      {/* Zig-Zag Bottom SVG */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          height: "20px",
        }}
      >
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 100 30"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 0 L5 24 L10 0 L15 24 L20 0 L25 24 L30 0 L35 24 L40 0 L45 24 L50 0 L55 24 L60 0 L65 24 L70 0 L75 24 L80 0 L85 24 L90 0 L95 24 L100 0 L100 24 L0 24 Z"
            fill="#928585c2"
          />
        </svg>
      </Box>
    </Box>
  );
};

export default TicketReceipt;
